import { cloneDeep } from 'lodash'

import type { ConfigLayerCapabilityOptions } from './ConfigLayerCapability'
import type { WMSCapability, WMSLayer } from './ConfigLayerCapabilityWMS'
import type { ServerType } from './ServerType'
import type { ConfigLayerOptions } from '../ConfigLayer'

import * as PoziUtils from 'api/utils'
import { getResolutionForScaleTerrestris } from 'utils/MapUtils'

import KeywordsParser from './KeywordsParser'
import { ConfigLayerType } from '../ConfigLayerType'

export interface ConfigLayerWMSOptions extends ConfigLayerOptions {
  parent?: string
  config: {
    url: string
    params: ConfigLayerWMSParams
    projection?: string
    proxyUrl?: string
    spatial: ConfigLayerWMSSpatial
    tileSize?: number
    transformer?: unknown
  }
  about: {
    description: string
    source: string
    organisation: string
    url?: string
  }
  layers?: ConfigLayerWMSOptions[]
  legend: string
  opacity?: number
  queryable: boolean
  serviceUrl?: string | null
}

interface ConfigLayerWMSParams {
  LAYERS: string
  SERVERTYPE?: ServerType
  STYLES: string
}

interface ConfigLayerWMSSpatial {
  label?: string
}

export class ConfigLayerWMS {
  static getLayer (capabilityOptions: ConfigLayerCapabilityOptions, wmsCapability: WMSCapability, layer: WMSLayer, index: number): ConfigLayerWMSOptions {
    // console.debug('ConfigLayerWMS - getLayer', capabilityOptions, wmsCapability, layer)

    const proxyUrl = PoziUtils.getLayerProxyUrl(capabilityOptions)

    const title = this.getTitle(capabilityOptions, index, layer)
    const titleid = this.getTitleId(capabilityOptions, index, title)
    const source = this.getSource(layer)
    const geometryType = this.getGeometryType(layer)
    const label = this.getDisplayField(layer)

    const serviceUrl = this.getServiceUrl(wmsCapability)

    let legend = this.getLegendUrl(layer)
    // Prepend proxy URL if available
    if (legend && proxyUrl) {
      legend = `${proxyUrl}${legend}`
    }

    const keywords = this.getKeywords(layer)
    const configFromKeywords = this.getConfigFromKeywords(titleid, keywords)

    // Keep a copy of the originial configFromKeywords
    const configFromKeywordsOriginal = cloneDeep(configFromKeywords)

    // Use group from keywords if set
    const group = configFromKeywords.group || ''
    const groupid = group ? PoziUtils.getPoziIDFromString(group) : ''
    delete configFromKeywords.group

    const maxResolution = this.getMaxResolution(layer)
    const minResolution = this.getMinResolution(layer)
    const opacity = this.getOpacity(capabilityOptions, layer)
    const queryable = this.getQueryable(capabilityOptions, layer)

    const description = configFromKeywords.description

    // Allow url override from keywords
    let url = configFromKeywords.dataUrl || this.getUrl(wmsCapability)
    delete configFromKeywords.dataUrl

    const urlIsRelative = url && !url.match(/^(?:[a-z+]+:)?\/\//i)
    if (urlIsRelative) {
      // Create a new absolute URL using the URL of the catalogue as the base
      try {
        const capabilitiesUrl = capabilityOptions.config?.spatial?.url
        url = (new URL(url, capabilitiesUrl)).href
      } catch (error) {
        console.log('ConfigLayerWMS - relative URL error', titleid, url, capabilityOptions)
      }
    }

    // Prepend proxy URL if available
    if (url && proxyUrl) {
      url = `${proxyUrl}${url}`
    }

    const organisation = this.getOrganisation(wmsCapability)
    const aboutUrl = this.getAboutUrl(wmsCapability)

    // Check if we are dealing with a tiled WMS service. If not specified,
    // we will use ImageWMS by default. If 'tileSize' is set, we will assume tiled to be true.
    const tiled = capabilityOptions.config?.spatial?.tiled || capabilityOptions.config?.spatial?.tileSize || false
    const type = tiled ? ConfigLayerType.TILE_WMS : ConfigLayerType.IMAGE_WMS

    // Combine layer params from capability and keyword options
    const params = {
      ...this.getParams(capabilityOptions, layer),
      ...configFromKeywords.parameter
    }

    // Remove 'parameter' from keywordOptions now that we have used it
    delete configFromKeywords.parameter

    // Use projection from configuration, if provided
    const projection = capabilityOptions.config?.spatial?.projection

    // Use profiles from catalogue configuration
    const profiles = capabilityOptions.profiles

    const tileSize = capabilityOptions.config?.spatial?.tileSize

    // Special case: if no parent has been set, we'll default it to 'Whats here' in order to show
    // up in the details panel when a user clicks on the map, but only for queryable layers
    //
    // TODO:This is a workaround until the followin gets resolved:
    //      https://trello.com/c/NuPIDgSL/18-enable-wms-layers-getfeatureinfo-results-to-be-displayed-in-info-panel-by-default
    const hasChildLayers = !!layer.Layer
    const parent = queryable && !hasChildLayers ? 'Whats here' : ''

    const configLayerWMSOptions: ConfigLayerWMSOptions = {
      group,
      groupid,
      title,
      titleid,
      queryable,
      parent,
      profiles,
      type,
      config: {
        url,
        params,
        projection,
        proxyUrl,
        spatial: {
          label
        },
        tileSize
      },
      configFromKeywords: configFromKeywordsOriginal,
      about: {
        description,
        source,
        organisation,
        url: aboutUrl
      },
      geometryType,
      legend,
      linkedLayers: capabilityOptions.linkedLayers,
      maxResolution,
      minResolution,
      opacity,
      localDataSource: capabilityOptions.localDataSource,
      serverType: capabilityOptions.serverType,
      serviceUrl,
      showInLayerControl: capabilityOptions.showInLayerControl,
      showLegend: capabilityOptions.showLegend,

      // Merge in any settings from the keyword option to override the above
      ...configFromKeywords
    }

    // console.debug('ConfigLayerWMS - getLayer config', titleid, configLayerWMSOptions)

    return configLayerWMSOptions
  }

  private static getKeywords (layer: any) {
    const keywords: string[] = layer?.KeywordList?.Keyword || layer?.KeywordList || []

    // console.debug('ConfigLayerWMS getKeywords', layer?.Title, keywords)

    return keywords
  }

  private static getConfigFromKeywords (titleid: string, keywords: string[]) {
    // console.debug('ConfigLayerWMS getConfigFromKeywords', keywords)

    // Keyword options are special config settings set in the keywords of a layer
    // since that currently is the only way to configure overrides in QGIS
    const configOptions = KeywordsParser.getOptions(keywords)

    // console.debug('ConfigLayerWMS getConfigFromKeywords - keyword options', titleid, configOptions)

    return configOptions
  }

  // This field is used as the 'label' field in a What's Here info panel
  private static getDisplayField (wmsLayer: WMSLayer) {
    const displayField = wmsLayer?.displayField

    // console.debug('getDisplayField: ', displayField, wmsLayer)

    return displayField
  }

  // This field is used as the 'label' field in a What's Here info panel
  private static getGeometryType (wmsLayer: WMSLayer) {
    const geometryType = wmsLayer?.geometryType

    // console.debug('getGeometryType: ', geometryType, wmsLayer)

    return geometryType
  }

  private static getParams (capabilityOptions: ConfigLayerCapabilityOptions, layer: WMSLayer): ConfigLayerWMSParams {
    const name = this.getName(layer)
    const style = this.getStyle(layer)

    // Get config-provided params
    let capabilityParams = capabilityOptions?.config?.spatial?.params || []

    // Make any config-provided params all UPPERCASE
    capabilityParams = Object.fromEntries(Object.entries(capabilityParams)
      .map(([key, value]) => [key.toUpperCase(), value]))

    const params = {
      LAYERS: name,
      SERVERTYPE: capabilityOptions.serverType,
      STYLES: style,

      // Merge standard params and config-defined params
      ...capabilityParams
    } as ConfigLayerWMSParams

    // console.debug('getParams', params)

    return params
  }

  private static getTitle (capabilityOptions: ConfigLayerCapabilityOptions, index: number, layer: WMSLayer): string {
    // Force value to be string due to fast-xml-parser not knowing what type it should be
    const title = String(layer?.Title ?? `${capabilityOptions.title}_${index}`)
    // console.debug('getTitle: ', title)
    return title
  }

  private static getTitleId (capabilityOptions: ConfigLayerCapabilityOptions, index: number, title: string): string {
    // Force value to be string due to fast-xml-parser not knowing what type it should be
    const titleid = PoziUtils.getPoziIDFromString(title) || `${capabilityOptions.title}_${index}`
    // console.debug('getTitleId: ', titleid)
    return titleid
  }

  private static getSource (wmsLayer: WMSLayer): string {
    const source: string = wmsLayer?.Abstract
    // console.debug('getSource: ', source)
    return source
  }

  private static getMaxResolution (wmsLayer: WMSLayer): number | undefined {
    const maxScaleDenominator = wmsLayer?.MaxScaleDenominator

    // Use getResolutionForScaleTerrestris to make sure that the scale/resolution calculation aligns
    // with the way GeoStyler does it to avoid discrepancies (as the OL scalebar calculation gives
    // different results)
    //
    // TODO: figure out why they differ!
    //
    // Force value to be number due to fast-xml-parser not knowing what type it should be
    const maxResolution = maxScaleDenominator ? getResolutionForScaleTerrestris(Number(maxScaleDenominator)) : undefined

    // console.debug('getMaxResolution: maxScaleDenominator/maxResolution', maxScaleDenominator, maxResolution)

    return maxResolution
  }

  private static getMinResolution (wmsLayer: WMSLayer): number | undefined {
    const minScaleDenominator = wmsLayer?.MinScaleDenominator

    // Use getResolutionForScaleTerrestris to make sure that the scale/resolution calculation aligns
    // with the way GeoStyler does it to avoid discrepancies (as the OL scalebar calculation gives
    // different results)
    //
    // TODO: figure out why they differ!
    //
    // Force value to be number due to fast-xml-parser not knowing what type it should be
    const minResolution = minScaleDenominator ? getResolutionForScaleTerrestris(Number(minScaleDenominator)) : undefined

    // console.debug('getMinResolution: minScaleDenominator/minResolution', minScaleDenominator, minResolution)

    return minResolution
  }

  private static getName (wmsLayer: WMSLayer): string {
    // Force value to be string due to fast-xml-parser not knowing what type it should be
    const name = String(wmsLayer?.Name || '')
    // console.debug('getName: ', name)
    return name
  }

  private static getStyle (wmsLayer: WMSLayer): string {
    const style = wmsLayer?.Style?.[0]?.Name

    // console.debug('getStyle: ', style)

    return style
  }

  private static getOpacity (capabilityOptions: ConfigLayerCapabilityOptions, layer: WMSLayer) {
    // Force value to be number due to fast-xml-parser not knowing what type it should be
    const opacity = Number(capabilityOptions.opacity ?? layer?.opacity ?? 1)

    // console.debug('getOpacity: ', opacity)

    return opacity
  }

  private static getQueryable (capabilityOptions: ConfigLayerCapabilityOptions, wmsLayer: WMSLayer): boolean {
    const catalogueQueryable = capabilityOptions.queryable

    // Force value to be boolean due to fast-xml-parser not knowing what type it should be.
    // We do this by forcing it to be a number first as that's how it's represented in the XML
    // but as a string.
    const layerQueryable = Boolean(Number(wmsLayer?.queryable ?? 1))

    // The queryable setting in the catalogue (if provided) overrides the layer setting
    const queryable = catalogueQueryable ?? layerQueryable

    // console.debug('getQueryable: ', catalogueQueryable, layerQueryable, queryable, wmsLayer)

    return queryable
  }

  private static getLegendUrl (wmsLayer: WMSLayer): string {
    const legendOnlineResource = wmsLayer?.Style?.[0]?.LegendURL?.[0]?.OnlineResource

    // Cater for generic parsing of XML using fast-xml-parser
    // In this case OnlineResource = { href: '....' } instead of a string
    let legendUrl = legendOnlineResource?.href || legendOnlineResource

    // Modify legend URL to be transparent and not have a title
    if (legendUrl) {
      try {
        const urlObject = new URL(legendUrl)

        urlObject.searchParams.set('LAYERTITLE', 'FALSE')
        urlObject.searchParams.set('RULELABEL', 'AUTO')
        urlObject.searchParams.set('TRANSPARENT', 'TRUE')

        legendUrl = urlObject.href
      } catch (error) {
        console.log('ConfigLayerWMS - getLegend - invalid legend URL', legendUrl)
      }
    }

    // console.debug('getLegend: ', legendUrl, wmsLayer)

    return legendUrl
  }

  private static getUrl (wmsCapability: WMSCapability) {
    const onlineResource = wmsCapability.Capability?.Request?.GetMap?.DCPType?.[0]?.HTTP?.Get?.OnlineResource

    // Cater for generic parsing of XML using fast-xml-parser
    // In this case OnlineResource = { href: '....' } instead of a string
    // @ts-expect-error
    const url = onlineResource?.href || onlineResource

    // console.debug('getUrl: ', url)

    return url
  }

  private static getOrganisation (wmsCapability: WMSCapability) {
    const organisation: string = wmsCapability.Service?.ContactInformation?.ContactPersonPrimary?.ContactOrganization
    return organisation
  }

  private static getAboutUrl (wmsCapability: WMSCapability) {
    // FIXME: This should be accessed from the layer, not the WFS Capabilities
    // url: "https://services.land.vic.gov.au/SpatialDatamart/dataSearchViewMetadata.html?anzlicId=ANZVI0803002910&publicId=guest&extractionProviderId=1"
    const aboutUrl = ''
    return aboutUrl
  }

  private static getServiceUrl (wmsCapability: WMSCapability) {
    const serviceUrl = wmsCapability?.Service?.OnlineResource?.href || null

    // console.debug('ConfigLayerWMS - getServiceUrl', serviceUrl, wmsCapability)

    return serviceUrl
  }
}
